import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function formatMoney(n, decimals = 2) {
  return `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

const RADIUS = 15.9;

export default function CostBreakdownDonut({ savings, tokenUsage }) {
  const manual = savings?.total_manual_cost ?? 0;
  const llm = tokenUsage?.total_llm_cost ?? savings?.total_llm_cost ?? 0;
  const saved = savings?.total_savings ?? Math.max(manual - llm, 0);
  const baseline = manual > 0 ? manual : saved + llm;
  const savingsPct = baseline > 0 ? (saved / baseline) * 100 : 0;
  const llmPct = baseline > 0 ? (llm / baseline) * 100 : 0;

  const circumference = 2 * Math.PI * RADIUS;
  const startOffset = circumference / 4;
  const savingsArc = (savingsPct / 100) * circumference;
  const llmArc = (llmPct / 100) * circumference;
  const minVisibleArc = circumference * 0.004;
  const showLlmArc = llm > 0 && llmArc >= minVisibleArc;

  return (
    <Card data-testid="bv-donut">
      <CardHeader>
        <CardTitle>Where the money goes</CardTitle>
        <CardDescription>Savings vs LLM spend from the manual baseline</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 py-4">
        <div className="relative h-44 w-44">
          <svg viewBox="0 0 42 42" className="h-full w-full -rotate-90">
            <circle cx="21" cy="21" r={RADIUS} fill="none" stroke="#eef0f3" strokeWidth="6" />
            {baseline > 0 && (
              <circle
                cx="21"
                cy="21"
                r={RADIUS}
                fill="none"
                stroke="#10b981"
                strokeWidth="6"
                strokeLinecap="butt"
                strokeDasharray={`${savingsArc} ${Math.max(circumference - savingsArc, 0)}`}
                strokeDashoffset={startOffset}
              />
            )}
            {showLlmArc && (
              <circle
                cx="21"
                cy="21"
                r={RADIUS}
                fill="none"
                stroke="#2f6fed"
                strokeWidth="6"
                strokeLinecap="butt"
                strokeDasharray={`${llmArc} ${Math.max(circumference - llmArc, 0)}`}
                strokeDashoffset={startOffset - savingsArc}
              />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-extrabold text-cp-ok">
              {savingsPct.toFixed(1)}%
            </span>
            <span className="text-xs text-muted-foreground">is savings</span>
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm bg-cp-ok" />
            Savings {formatMoney(saved)}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm bg-cp-indigo" />
            LLM {formatMoney(llm, llm < 1 ? 4 : 2)}
            {llm > 0 && !showLlmArc && (
              <span className="text-[10px] text-muted-foreground/80">(too small to show on ring)</span>
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
